import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { patientHistoryService } from '../services/patientHistoryService'
import type { PatientPreferences as PatientPreferencesType } from '../types/patientHistory'
import { PreferencesFormData } from '../types/patientHistory'
import './PatientPreferences.css'

interface PatientPreferencesProps {
  patientId: string
  preferences: PatientPreferencesType
}

export function PatientPreferences({ patientId, preferences }: PatientPreferencesProps) {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const {
    register,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      preferredLanguage: preferences.preferredLanguage || '',
      spokenLanguages: preferences.spokenLanguages?.join(', ') || '',
      religion: preferences.religion || '',
      culturalBackground: preferences.culturalBackground || '',
      dietaryPreferences: preferences.dietaryPreferences?.join(', ') || '',
      communicationPreferences: {
        preferredMethod: preferences.communicationPreferences?.preferredMethod || '',
        preferredContactTime: preferences.communicationPreferences?.preferredContactTime || '',
        requiresInterpreter: preferences.communicationPreferences?.requiresInterpreter || false,
        interpreterLanguage: preferences.communicationPreferences?.interpreterLanguage || '',
        smsConsent: preferences.communicationPreferences?.smsConsent || false,
        smsOptOut: !!preferences.communicationPreferences?.smsOptOutAt,
      },
      accessibilityNeeds: preferences.accessibilityNeeds?.join(', ') || '',
      privacyPreferences: {
        canShareWithFamily: preferences.privacyPreferences?.canShareWithFamily || false,
        preferredFamilyContact: preferences.privacyPreferences?.preferredFamilyContact || '',
        restrictInformationSharing: preferences.privacyPreferences?.restrictInformationSharing || false,
      },
    },
  })

  const requiresInterpreter = watch('communicationPreferences.requiresInterpreter') || false
  const smsOptOut = watch('communicationPreferences.smsOptOut') || false

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PreferencesFormData>) =>
      patientHistoryService.updatePreferences(patientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
      setIsEditing(false)
    },
  })

  const onSubmit = (data: any) => {
    const now = new Date().toISOString()
    const existingSmsConsentRecordedAt = preferences.communicationPreferences?.smsConsentRecordedAt
    const existingSmsOptOutAt = preferences.communicationPreferences?.smsOptOutAt
    const smsConsent = !!data.communicationPreferences?.smsConsent
    const smsOptOutSelected = !!data.communicationPreferences?.smsOptOut
    const smsConsentRecordedAt = smsConsent
      ? (existingSmsConsentRecordedAt || now)
      : undefined
    const smsOptOutAt = smsOptOutSelected
      ? (existingSmsOptOutAt || now)
      : undefined

    // Convert string fields back to arrays and ensure proper structure
    const submitData: Partial<PreferencesFormData> = {
      preferredLanguage: data.preferredLanguage || undefined,
      spokenLanguages: data.spokenLanguages 
        ? (typeof data.spokenLanguages === 'string' 
          ? data.spokenLanguages.split(',').map((s: string) => s.trim()).filter(Boolean)
          : data.spokenLanguages)
        : undefined,
      religion: data.religion || undefined,
      culturalBackground: data.culturalBackground || undefined,
      dietaryPreferences: data.dietaryPreferences
        ? (typeof data.dietaryPreferences === 'string'
          ? data.dietaryPreferences.split(',').map((s: string) => s.trim()).filter(Boolean)
          : data.dietaryPreferences)
        : undefined,
      accessibilityNeeds: data.accessibilityNeeds
        ? (typeof data.accessibilityNeeds === 'string'
          ? data.accessibilityNeeds.split(',').map((s: string) => s.trim()).filter(Boolean)
          : data.accessibilityNeeds)
        : undefined,
      communicationPreferences: data.communicationPreferences ? {
        preferredMethod: data.communicationPreferences.preferredMethod || undefined,
        preferredContactTime: data.communicationPreferences.preferredContactTime || undefined,
        requiresInterpreter: data.communicationPreferences.requiresInterpreter || undefined,
        interpreterLanguage: data.communicationPreferences.interpreterLanguage || undefined,
        smsConsent,
        smsConsentRecordedAt,
        smsOptOutAt,
      } : undefined,
      privacyPreferences: data.privacyPreferences ? {
        canShareWithFamily: data.privacyPreferences.canShareWithFamily || undefined,
        preferredFamilyContact: data.privacyPreferences.preferredFamilyContact || undefined,
        restrictInformationSharing: data.privacyPreferences.restrictInformationSharing || undefined,
      } : undefined,
    }
    updateMutation.mutate(submitData)
  }

  if (isEditing) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="patient-preferences-form">
        <div className="preferences-section">
          <h3>Language & Communication</h3>
          <div className="form-group">
            <label htmlFor="preferredLanguage">Preferred Language</label>
            <input
              id="preferredLanguage"
              type="text"
              {...register('preferredLanguage')}
              placeholder="e.g., en-IE, ga-IE"
            />
          </div>
          <div className="form-group">
            <label htmlFor="spokenLanguages">Spoken Languages (comma-separated)</label>
            <input
              id="spokenLanguages"
              type="text"
              {...register('spokenLanguages')}
              placeholder="e.g., en-IE, ga-IE"
            />
          </div>
          <div className="form-group">
            <label htmlFor="communicationMethod">Preferred Communication Method</label>
            <select id="communicationMethod" {...register('communicationPreferences.preferredMethod')}>
              <option value="">Not specified</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="post">Post</option>
              <option value="in-person">In Person</option>
              <option value="sms">SMS</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="contactTime">Preferred Contact Time</label>
            <input
              id="contactTime"
              type="text"
              {...register('communicationPreferences.preferredContactTime')}
              placeholder="e.g., Morning, Afternoon"
            />
          </div>
          <div className="form-group checkbox-group">
            <input
              id="requiresInterpreter"
              type="checkbox"
              {...register('communicationPreferences.requiresInterpreter')}
            />
            <label htmlFor="requiresInterpreter">Requires Interpreter</label>
          </div>
          <div className="form-group checkbox-group">
            <input
              id="smsConsent"
              type="checkbox"
              {...register('communicationPreferences.smsConsent')}
            />
            <label htmlFor="smsConsent">Allow SMS reminders</label>
          </div>
          <div className="form-group checkbox-group">
            <input
              id="smsOptOut"
              type="checkbox"
              {...register('communicationPreferences.smsOptOut')}
            />
            <label htmlFor="smsOptOut">SMS opt-out active</label>
          </div>
          {smsOptOut && (
            <div className="form-group">
              <div className="field-hint">
                Patient has opted out of SMS reminders. Clear to allow SMS if consent is valid.
              </div>
            </div>
          )}
          {requiresInterpreter && (
            <div className="form-group">
              <label htmlFor="interpreterLanguage">Interpreter Language</label>
              <input
                id="interpreterLanguage"
                type="text"
                {...register('communicationPreferences.interpreterLanguage')}
                placeholder="e.g., Polish, Romanian"
              />
            </div>
          )}
        </div>

        <div className="preferences-section">
          <h3>Cultural & Religious</h3>
          <div className="form-group">
            <label htmlFor="religion">Religion</label>
            <input
              id="religion"
              type="text"
              {...register('religion')}
              placeholder="e.g., Roman Catholic, Church of Ireland, None"
            />
          </div>
          <div className="form-group">
            <label htmlFor="culturalBackground">Cultural Background</label>
            <input
              id="culturalBackground"
              type="text"
              {...register('culturalBackground')}
              placeholder="e.g., Irish, Polish, Romanian"
            />
          </div>
        </div>

        <div className="preferences-section">
          <h3>Dietary & Accessibility</h3>
          <div className="form-group">
            <label htmlFor="dietaryPreferences">Dietary Preferences (comma-separated)</label>
            <input
              id="dietaryPreferences"
              type="text"
              {...register('dietaryPreferences')}
              placeholder="e.g., Vegetarian, Halal, Kosher"
            />
          </div>
          <div className="form-group">
            <label htmlFor="accessibilityNeeds">Accessibility Needs (comma-separated)</label>
            <input
              id="accessibilityNeeds"
              type="text"
              {...register('accessibilityNeeds')}
              placeholder="e.g., Wheelchair access, Hearing aid, Large print"
            />
          </div>
        </div>

        <div className="preferences-section">
          <h3>Privacy Preferences</h3>
          <div className="form-group checkbox-group">
            <input
              id="canShareWithFamily"
              type="checkbox"
              {...register('privacyPreferences.canShareWithFamily')}
            />
            <label htmlFor="canShareWithFamily">Can share information with family</label>
          </div>
          <div className="form-group">
            <label htmlFor="preferredFamilyContact">Preferred Family Contact</label>
            <input
              id="preferredFamilyContact"
              type="text"
              {...register('privacyPreferences.preferredFamilyContact')}
              placeholder="Name or relationship"
            />
          </div>
          <div className="form-group checkbox-group">
            <input
              id="restrictInformationSharing"
              type="checkbox"
              {...register('privacyPreferences.restrictInformationSharing')}
            />
            <label htmlFor="restrictInformationSharing">Restrict information sharing</label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="btn-secondary"
            disabled={updateMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </form>
    )
  }

  return (
    <div className="patient-preferences-view">
      <div className="preferences-header">
        <h3>Current Preferences</h3>
        <button className="btn-secondary btn-small" onClick={() => setIsEditing(true)}>
          Edit Preferences
        </button>
      </div>

      <div className="preferences-grid">
        <div className="preference-item">
          <strong>Preferred Language:</strong>{' '}
          {preferences.preferredLanguage || 'Not specified'}
        </div>
        {preferences.spokenLanguages && preferences.spokenLanguages.length > 0 && (
          <div className="preference-item">
            <strong>Spoken Languages:</strong>{' '}
            {preferences.spokenLanguages.join(', ')}
          </div>
        )}
        {preferences.religion && (
          <div className="preference-item">
            <strong>Religion:</strong> {preferences.religion}
          </div>
        )}
        {preferences.culturalBackground && (
          <div className="preference-item">
            <strong>Cultural Background:</strong> {preferences.culturalBackground}
          </div>
        )}
        {preferences.dietaryPreferences && preferences.dietaryPreferences.length > 0 && (
          <div className="preference-item">
            <strong>Dietary Preferences:</strong>{' '}
            {preferences.dietaryPreferences.join(', ')}
          </div>
        )}
        {preferences.communicationPreferences?.preferredMethod && (
          <div className="preference-item">
            <strong>Preferred Communication:</strong>{' '}
            {preferences.communicationPreferences.preferredMethod}
          </div>
        )}
        {preferences.communicationPreferences?.requiresInterpreter && (
          <div className="preference-item">
            <strong>Interpreter Required:</strong> Yes
            {preferences.communicationPreferences.interpreterLanguage && (
              <> ({preferences.communicationPreferences.interpreterLanguage})</>
            )}
          </div>
        )}
        {preferences.communicationPreferences?.smsConsent !== undefined && (
          <div className="preference-item">
            <strong>SMS Consent:</strong>{' '}
            {preferences.communicationPreferences.smsConsent ? 'Granted' : 'Not granted'}
          </div>
        )}
        {preferences.communicationPreferences?.smsOptOutAt && (
          <div className="preference-item">
            <strong>SMS Opt-Out:</strong> Active ({new Date(preferences.communicationPreferences.smsOptOutAt).toLocaleDateString()})
          </div>
        )}
        {preferences.privacyPreferences?.canShareWithFamily !== undefined && (
          <div className="preference-item">
            <strong>Share with Family:</strong>{' '}
            {preferences.privacyPreferences.canShareWithFamily ? 'Yes' : 'No'}
            {preferences.privacyPreferences.preferredFamilyContact && (
              <> ({preferences.privacyPreferences.preferredFamilyContact})</>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
